/**
 * Job execution status returned by the asynchronous Order creation workflow.
 */
export enum CreateOrderJobStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/** Time interval between status poll requests in milliseconds. */
export const CREATE_ORDER_JOB_POLL_INTERVAL_MILLISECONDS: number = 1000;

/** Safety timeout for order creation polling in milliseconds (180 seconds). */
export const CREATE_ORDER_JOB_TIMEOUT_MILLISECONDS: number = 180_000;

/**
 * Single service item input for order creation.
 */
export interface CreateOrderItemInput {
  serviceId: string;
  ticketIds?: string[] | null;
}

export interface CreateOrderCreatedByInput {
  name: string;
  email: string;
}

export type OrderCommissionType = 'PERCENTAGE' | 'FIXED';

export interface CreateOrderCommissionInput {
  type: OrderCommissionType;
  value: number;
}

/**
 * Payload sent to createOrder GraphQL mutation.
 */
export interface CreateOrderInput {
  items: CreateOrderItemInput[];
  createdBy: CreateOrderCreatedByInput;
  scheduledPaymentDate?: string | null;
  /** Plazo para confirmar. Obligatorio si !asOfficial. */
  freezeDeadlineAt?: string | null;
  /** true: nace CONFIRMADO, sin preliminar. */
  asOfficial?: boolean | null;
  reservationNumber?: string | null;
  orderObservation?: string | null;
  commission?: CreateOrderCommissionInput | null;
}

export interface ConfirmPreliminaryOrderResult {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  displayCode: string;
  pdfStatus: string;
}

/**
 * Record representing an Order of Service created successfully by the backend.
 */
export interface CreatedOrder {
  orderId: string;
  orderNumber?: string | number | null;
  displayCode?: string | null;
  serviceIds?: string[];
  ticketIds?: string[] | null;
  idItinerary?: string | null;
  providerId?: string | null;
  currency?: string | null;
  orderStatus?: string | null;
  scheduledPaymentDate?: string | null;
  freezeDeadlineAt?: string | null;
  reservationNumber?: string | null;
  orderObservation?: string | null;
  pdfStatus?: string | null;
  baseAmount?: number | null;
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  total?: number | null;
}

/**
 * Record representing a service that could not be processed for order creation.
 */
export interface RejectedService {
  serviceId?: string | null;
  ticketId?: string | null;
  reason?: string | null;
  message?: string | null;
  userMessage?: string | null;
}

/**
 * Non-blocking advisory warning generated during order creation.
 */
export interface OrderWarning {
  serviceId?: string | null;
  groupKey?: string | null;
  reason?: string | null;
  message?: string | null;
  userMessage?: string | null;
}

/**
 * Result envelope returned by createOrder mutation or getCreateOrderJob query.
 */
export interface CreateOrderJobResult {
  jobId: string;
  status: string;
  errorCode?: string | null;
  lastError?: string | null;
  userMessage?: string | null;
  createdOrders?: CreatedOrder[];
  rejectedServices?: RejectedService[];
  warnings?: OrderWarning[];
}

/**
 * Normalized result format presented to user interfaces.
 */
export interface CreateOrderResult {
  createdOrders: CreatedOrder[];
  rejectedServices: RejectedService[];
  warnings: OrderWarning[];
  jobId?: string | null;
}

export function isCreateOrderJobInProgress(
  statusString: string | null | undefined,
): boolean {
  const normalizedStatus = statusString?.trim().toUpperCase();
  return (
    normalizedStatus === CreateOrderJobStatus.PENDING ||
    normalizedStatus === CreateOrderJobStatus.PROCESSING
  );
}

export function toCreateOrderResult(
  jobResultPayload: CreateOrderJobResult | null | undefined,
): CreateOrderResult {
  return {
    jobId: jobResultPayload?.jobId ?? null,
    createdOrders: jobResultPayload?.createdOrders ?? [],
    rejectedServices: jobResultPayload?.rejectedServices ?? [],
    warnings: jobResultPayload?.warnings ?? [],
  };
}
